import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import manifest from '#client/manifest';

import IntercomButton from '../Intercom';
import SharedAsset from '../SharedAsset';

const WrappedTypography = (props) => <Typography {...props} />;
const WrappedButton = (props) => <Button {...props} />;

const ContactMethodsContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: 24,
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-around'
  }
}));

const ContactMethodContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: 48,
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'column',
    padding: '0 8px',
    width: 350
  }
}));

const ContactMethodContent = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  [theme.breakpoints.up('sm')]: {
    textAlign: 'center'
  }
}));

const HelpIcon = styled(SharedAsset)(({ theme }) => ({
  width: 48,
  [theme.breakpoints.down('sm')]: {
    marginRight: 24
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: 16,
    width: 80
  }
}));

const ContactTitle = styled(WrappedTypography)(({ theme }) => ({
  fontColor: theme.palette.text.primary,
  ...theme.typography.body_smallBold
}));

const ContactBody = styled(WrappedTypography)(({ theme }) => ({
  ...theme.typography.body_smallRegular
}));

const CTALink = styled(WrappedButton)(({ theme }) => ({
  margin: 0,
  justifyContent: 'unset',
  padding: 0,
  ...theme.typography.body_smallBold,
  '&:focus, &:active, &:hover': {
    boxShadow: 'none',
    '& span': {
      textDecoration: 'underline'
    }
  },
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'center'
  }
}));

const CTAIntercom = styled(IntercomButton)(({ theme }) => ({
  margin: 0,
  justifyContent: 'center',
  padding: 0,
  ...theme.typography.body_smallBold,
  '&:focus, &:active, &:hover': {
    boxShadow: 'none',
    '& span': {
      textDecoration: 'underline'
    }
  }
}));

const methods = [
  ...(window.Intercom ? [{
    icon: <HelpIcon src="/icon/misc/chat-help-256.svg" height={120} alt="chat" />,
    type: 'chat',
    name: 'Chat with Us!',
    cta: <CTAIntercom color="primary">Chat Now</CTAIntercom>,
    description: `We are always happy to chat with you. Our chat hours are ${manifest.contactHours}.`
  }] : []),
  {
    icon: <HelpIcon src="/icon/misc/call-help-256.svg" height={120} alt="call" />,
    type: 'phone',
    name: 'Call Us!',
    cta: <CTALink variant="text" href="tel:844-493-6249" color="primary">1-844-4-ZENBIZ</CTALink>,
    description: `Call us, and we can answer your questions. Our phone hours are ${manifest.contactHours}.`
  },
  {
    icon: <HelpIcon src="/icon/misc/email-help-256.svg" height={120} alt="email" />,
    type: 'email',
    name: 'Send Us an Email!',
    cta: <CTALink variant="text" href="mailto:support@zenbusiness.com" color="primary">support@zenbusiness.com</CTALink>,
    description: 'Send us an email whenever you want, our sales representative will email you back within 1 business\u00A0day.'
  }
];

const showContactMethod = (showMethods, type) => (
  showMethods.length === 0
  || (
    showMethods?.length > 0
    && showMethods.includes(type)
  )
);

const ContactMethods = ({
  showIcons,
  showMethods,
  headingLevel
}) => (
  <ContactMethodsContainer>
    {methods.map(({
      cta,
      description,
      icon: Icon,
      name,
      type
    }) => (
      showContactMethod(showMethods, type) && (
        <ContactMethodContainer key={name}>
          {showIcons && Icon}
          <ContactMethodContent>
            <ContactTitle variant={headingLevel}>
              {name}
            </ContactTitle>
            {cta}
            <ContactBody variant="body1">
              {description}
            </ContactBody>
          </ContactMethodContent>
        </ContactMethodContainer>
      )
    ))}
  </ContactMethodsContainer>
);

ContactMethods.propTypes = {
  showIcons: PropTypes.bool,
  showMethods: PropTypes.arrayOf(PropTypes.string),
  headingLevel: PropTypes.oneOf(['h3', 'h4', 'h5', 'h6'])
};

ContactMethods.defaultProps = {
  showIcons: true,
  showMethods: [],
  headingLevel: 'h4'
};

export default ContactMethods;
